import React, { Fragment, useMemo, useState } from 'react'
import { Dialog } from '../components/ui/dialog'
import { Table } from '../components/ui/Table'
import { useSelector } from 'react-redux'
import { axiosGet } from '../util/api'
import { Filters } from '../config'
import { Layout } from '../components/Layout'
import { LoadFunc, useTableHooks } from '../packages/blueprint-components/src/useTableHooks'
import { AuditLog, AuditLogI } from '../components/audit_log/AuditLog'
import { RelativeTimestamp } from '../components/ui/RelativeTimestamp'
import { Link } from 'react-router-dom'
import { State } from '../reducers'
import { Tooltip } from '../components/ui/tooltip'
import { Column } from '../types/table'
import { BreadcrumbProps } from '@blueprintjs/core'
import type { EntityId } from '../types/constants'

const filters = [
    Filters.AuditLogEffectedEntityType,
    Filters.AuditLogSourceType,
    Filters.AuditLogEffectedEntities,
]

export const AuditLogsTable = ({
    targetId = null,
    targetType = null,
    showHeader = true,
    nested = false,
    bootstrapped = false,
    leadingActions = null,
}) => {
    const [modal, setModal] = useState<AuditLog | null>(null)
    const breadcrumbs: BreadcrumbProps[] = [
        { text: 'Overview' },
        { href: '/combined-history', text: 'History' },
    ]

    const profile = useSelector<State, EntityId | null>((state) => state.user.profile)
    const load: LoadFunc<AuditLog> = (params: any) => {
        if (!profile) {
            return Promise.resolve()
        }

        return axiosGet('audit-logs', {
            ...params,
            filter: {
                ...params.filter,
                target_type_id: params.filter?.target_type_id || targetType,
                target_id: params.filter?.target_id || targetId,
            },
        }).then((res) => {
            if (res?.data?.data) {
                res.data.data = res.data.data.map((i: AuditLogI) => new AuditLog(i))
            }

            return Promise.resolve(res)
        })
    }
    const tableHooks = useTableHooks(load, {}, true)

    const columns: Column<AuditLog, any>[] = useMemo(() => {
        return [
            {
                key: 'created_at',
                title: 'Date',
                cell: (log) => <RelativeTimestamp date={new Date(log.created_at)} />,
            },
            {
                key: 'source_type_id',
                title: 'Source',
                cell: (log) => log.sourceType(),
                sortable: false,
                width: 150,
            },
            {
                key: 'source_info',
                title: 'Source Info',
                cell: (log) => log.sourceInfo(),
                sortable: false,
                width: 300,
            },
            {
                key: 'effected_entities',
                title: 'Effected Entities',
                cell: (log) => log.effectedEntities(),
                width: 300,
            },
            {
                key: 'event',
                sortable: false,
                cell: (log) => {
                    if (
                        log.meta.hasOwnProperty('event') &&
                        // @ts-ignore
                        log.meta.event.hasOwnProperty('actions') &&
                        // @ts-ignore
                        log.meta.event.actions.hasOwnProperty('bulk_action_failed')
                    ) {
                        return (
                            <Tooltip
                                openOnTargetFocus={false}
                                content={`Some of the ${log.targetType()?.toLowerCase()}s in the bulk action may have been affected.`}
                            >
                                Bulk Action Failed
                            </Tooltip>
                        )
                    }
                    return <Link to={`/audit-logs/${log.id}`}>{log.event()}</Link>
                },
                width: 300,
            },
        ]
    }, [setModal])

    const tableOptions = {
        name: 'Audit Logs',
        showHeader: false,
        className: showHeader ? null : 'mt-0 mb-4',
        cardClassName: 'table-header-top-border',
        tableHooks: tableHooks,
        filters: targetType && targetId ? null : filters,
        leadingActions,
        dateSelect: true,
        columns: columns,
        onSelectChange: null,
        selectKey: 'campaign_id',
    }

    const table = () => (
        <>
            <Table {...tableOptions} />
            <Dialog isOpen={!!modal} onClose={() => setModal(null)} className="p-4">
                {modal && (
                    <Fragment>
                        <div className="">
                            <RelativeTimestamp
                                className="inline-block"
                                date={new Date(modal.created_at)}
                            />
                            <span>&nbsp;a&nbsp;</span>
                            <span>{modal.sourceType()}</span>
                            <span>&nbsp;{modal.event()}</span>
                            <span>&nbsp;on&nbsp;</span>
                            <span>{modal.effectedEntities()}.</span>
                        </div>
                        {JSON.stringify(modal)}
                    </Fragment>
                )}
            </Dialog>
        </>
    )

    return (
        <>
            {bootstrapped ? (
                table()
            ) : (
                <Layout breadcrumbs={breadcrumbs} primary="Audit Logs" nested={nested}>
                    {table()}
                </Layout>
            )}
        </>
    )
}
